import { LinearTransform, Rect } from '@st/geom'
import { useElementsInView } from '@st/react-util/use-intersection-observer'
import { deepEqual } from '@st/redux/src/deep-equal'
import { jsonMapDiff } from '@st/util/json-diff'
import {
  ComponentType,
  forwardRef,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore
} from 'react'
import {
  AnnotationPlugin,
  DeselectAnnotationEvent,
  SelectedAnnotationEvent
} from './annotation-plugin'
import { CanvasImage } from './canvas-image'
import {
  AnnotationBase,
  DocumentViewerState,
  PAGE_COLLAPSED_HEIGHT_PX,
  PAGE_VERTICAL_SPACING_PX,
  selActiveAnnotation,
  selActiveAnnotationId,
  selAnnotationById,
  selAnnotationsForPage,
  selPageTransform,
  selScrollAnnotationPosition,
  selScrollPagePositionY,
  ToolMessage,
  ToolStateBase
} from './document-viewer-module'
import { MuPDFRendererBackend } from './hooks/MuPDFViewerBackend'
import { PageStructure, ResolvedPageStructure } from './models'
import {
  useDocumentViewer,
  useDocumentViewerMessages,
  useDocumentViewerState
} from './use-document-viewer'
import { LinearTransformProvider, useLinearTransform } from './use-linear-transform'
import { useScrollPreservation } from './use-scroll-preservation'

export type PageToolbarProps = {
  page: PageStructure
}

export type PageDividerProps = {
  pageAbove?: PageStructure
  pageBelow?: PageStructure
}

type Props = {
  src: string
  annotations?: Record<string, AnnotationBase>
  plugins: AnnotationPlugin<AnnotationBase, ToolStateBase>[]
  createAnnotation: (annotation: AnnotationBase) => void
  updateAnnotation: (annotation: AnnotationBase) => void
  deleteAnnotation: (annotationId: string) => void
  PageToolbar?: ComponentType<PageToolbarProps>
  PageDivider?: ComponentType<PageDividerProps>
  deletedPageNumbers?: number[]
  pageRotations?: Record<number, number>
}

export function DocumentViewer({
  src,
  plugins,
  annotations,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
  PageToolbar,
  PageDivider,
  deletedPageNumbers,
  pageRotations
}: Props) {
  const backend = useMemo(() => new MuPDFRendererBackend({ documentUrl: src }), [src])

  useEffect(() => {
    return () => {
      backend.dispose()
    }
  }, [backend])

  const state = useSyncExternalStore(
    backend.subscribe.bind(backend),
    backend.getState.bind(backend)
  )

  const documentViewer = useDocumentViewer()
  const docViewerState = useDocumentViewerState((s) => s)

  const pages: ResolvedPageStructure[] = useMemo(() => {
    return state.documentStructure.pages.map((page): ResolvedPageStructure => {
      const rotation = pageRotations?.[page.pageNumber] ?? 0
      const isDeleted = deletedPageNumbers?.includes(page.pageNumber) ?? false
      const bounds = isDeleted
        ? {
            ...page.bounds,
            width: page.bounds.width,
            height: PAGE_COLLAPSED_HEIGHT_PX
          }
        : LinearTransform.apply(page.bounds, LinearTransform.pageRotate(rotation, page.bounds))

      return {
        ...page,
        sourceBounds: page.bounds,
        rotation: rotation,
        isDeleted: isDeleted,
        bounds: bounds
      }
    })
  }, [state.documentStructure.pages, pageRotations, deletedPageNumbers])

  const selectedAnnotationId = selActiveAnnotationId(docViewerState)

  function onAnnotationSelected(selectedAnnotationId: string) {
    // This ensures we don't have a stale state
    // We don't add onDeselectAnnotation to the effects array below
    // so we don't trigger on every state change
    const docViewerState = documentViewer.getState()

    const event: SelectedAnnotationEvent<AnnotationBase> = {
      type: 'selectedAnnotation',
      annotation: docViewerState.annotations[selectedAnnotationId],
      annotationDraft: docViewerState.annotationDrafts[selectedAnnotationId]
    }

    const resolvedAnnotation = event.annotationDraft || event.annotation

    const matchingPlugins = plugins.filter(
      (p) => resolvedAnnotation && p.handlesAnnotation?.(resolvedAnnotation)
    )

    matchingPlugins.forEach((plugin) => {
      plugin.onAnnotationSelected?.(event, documentViewer.send)
    })
  }

  function onAnnotationDeselected(deselectedAnnotationId: string) {
    // This ensures we don't have a stale state
    // We don't add onDeselectAnnotation to the effects array below
    // so we don't trigger on every state change
    const docViewerState = documentViewer.getState()

    const event: DeselectAnnotationEvent<AnnotationBase> = {
      type: 'deselectAnnotation',
      annotation: docViewerState.annotations[deselectedAnnotationId],
      annotationDraft: docViewerState.annotationDrafts[deselectedAnnotationId]
    }

    const resolvedAnnotation = event.annotationDraft || event.annotation

    const matchingPlugins = plugins.filter(
      (p) => p.name == resolvedAnnotation?.type && p.onAnnotationDeselected
    )

    matchingPlugins.forEach((plugin) => {
      plugin.onAnnotationDeselected!(event, documentViewer.send)
    })
  }

  useEffect(() => {
    if (selectedAnnotationId) {
      onAnnotationSelected(selectedAnnotationId)
    }

    return () => {
      if (selectedAnnotationId) {
        onAnnotationDeselected(selectedAnnotationId)
      }
    }
  }, [selectedAnnotationId])

  const transform = selPageTransform(docViewerState)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const zoomTrigger = useMemo(() => transform.a, [transform.a])
  const transformedVerticalPageSpacing = LinearTransform.apply(
    { x: 0, y: PAGE_VERTICAL_SPACING_PX },
    transform
  )

  useScrollPreservation(scrollContainerRef, [zoomTrigger])

  const { elementRef, elementIdentifiersInView } = useElementsInView({
    root: scrollContainerRef.current,
    elementIdentifierDataAttribute: 'pageNumber'
  })

  const pageNumbersToRender = useMemo(() => {
    return pagesToRender(elementIdentifiersInView.map(Number), pages)
  }, [elementIdentifiersInView, pages])

  useEffect(() => {
    documentViewer.send({
      type: 'remoteAnnotationsUpdated',
      changes: jsonMapDiff(docViewerState.annotations, annotations ?? {}, deepEqual)
    })
  }, [annotations])

  useDocumentViewerMessages((message) => {
    switch (message.type) {
      case 'scrollToPage':
        if (state.status == 'loaded') {
          scrollContainerRef.current?.scrollTo({
            behavior: 'smooth',
            top: selScrollPagePositionY(
              pages,
              selPageTransform(docViewerState),
              message.pageNumber,
              deletedPageNumbers
            )
          })
        }
        break

      case 'scrollToAnnotation':
        var annotation = selAnnotationById(docViewerState, message.annotationId)
        if (annotation) {
          scrollContainerRef.current?.scrollTo({
            behavior: 'smooth',
            top: selScrollAnnotationPosition(
              pages,
              selPageTransform(docViewerState),
              annotation,
              deletedPageNumbers
            ).y
          })
        }

        break
      // These messages should be forwarded to the top-level
      // because they deal with persistence of annotations
      // the document viewer does not dictate persistence layer
      case 'createAnnotation':
        createAnnotation(message.annotationDraft)
        break
      case 'updateAnnotation':
        updateAnnotation(message.annotationDraft)
        break
      case 'deleteAnnotation':
        deleteAnnotation(message.annotationId)
        break
    }
  })

  if (state.status === 'loading') {
    return <div />
  }

  return (
    <LinearTransformProvider transform={transform}>
      <div
        className="relative h-full w-full bg-stone-500"
        ref={scrollContainerRef}
        style={{ overflow: 'overlay' }}
      >
        <div>
          {pages.map((pageStructure, index) => {
            const annotationsForPage = selAnnotationsForPage(
              docViewerState,
              pageStructure.pageNumber
            )
            const nextPage = pages[index + 1]

            return (
              <Fragment key={pageStructure.pageNumber}>
                {/* Spacer above the very first page */}
                {pageStructure.pageNumber == 1 && (
                  <div
                    className="mx-auto"
                    style={{
                      height: transformedVerticalPageSpacing.y,
                      // Ensure the spacer spans the full scroll-area width (i.e. page width after zoom)
                      width: LinearTransform.apply(
                        { x: pageStructure.bounds.width, y: 0 },
                        transform
                      ).x
                    }}
                  >
                    {PageDivider && <PageDivider pageBelow={pageStructure} />}
                  </div>
                )}
                <PDFViewerPageViewer
                  ref={elementRef}
                  backend={backend}
                  pageStructure={pageStructure}
                  shouldRender={pageNumbersToRender.includes(pageStructure.pageNumber)}
                  inViewport={elementIdentifiersInView.includes(String(pageStructure.pageNumber))}
                  annotations={annotationsForPage as AnnotationBase[]}
                  toolState={docViewerState.toolStates[docViewerState.selectedToolName]}
                  activeAnnotationState={docViewerState.selectionState}
                  activeAnnotation={selActiveAnnotation(docViewerState)}
                  plugins={plugins}
                  PageToolbar={PageToolbar}
                  PageDivider={PageDivider}
                  send={(message) => {
                    // todo - simplify
                    // we should be able to forward these messages without so much boilerplate
                    switch (message.type) {
                      case 'selectAnnotation':
                        documentViewer.send({
                          type: 'selectAnnotation',
                          annotationId: message.annotationId
                        })
                        break
                      case 'deselectAnnotation':
                        documentViewer.send({ type: 'deselectAnnotation' })
                        break
                      case 'beginEditingAnnotation':
                        documentViewer.send({
                          type: 'beginEditingAnnotation',
                          annotationDraft: message.annotationDraft
                        })
                        break
                      case 'createAnnotationDraft':
                        documentViewer.send({
                          type: 'createAnnotationDraft',
                          annotationDraft: message.annotationDraft,
                          elementStatus: message.elementStatus
                        })
                        break
                      case 'updateAnnotationDraft':
                        documentViewer.send({
                          type: 'updateAnnotationDraft',
                          annotationDraft: message.annotationDraft
                        })
                        break
                      case 'discardAnnotationDraft':
                        documentViewer.send({
                          type: 'discardAnnotationDraft',
                          annotationId: message.annotationId
                        })
                        break
                      case 'updateTool':
                        documentViewer.send({ type: 'updateTool', tool: message.tool })
                        break
                      case 'createAnnotation':
                        documentViewer.send({
                          type: 'createAnnotation',
                          annotationDraft: message.annotationDraft
                        })
                        break
                      case 'updateAnnotation':
                        documentViewer.send({
                          type: 'updateAnnotation',
                          annotationDraft: message.annotationDraft
                        })
                        break
                      case 'deleteAnnotation':
                        documentViewer.send({
                          type: 'deleteAnnotation',
                          annotationId: message.annotationId
                        })
                        break
                      case 'scrollToPage':
                        documentViewer.send({
                          type: 'scrollToPage',
                          pageNumber: message.pageNumber
                        })
                        break
                      case 'scrollToAnnotation':
                        documentViewer.send({
                          type: 'scrollToAnnotation',
                          annotationId: message.annotationId
                        })
                        break
                    }
                  }}
                />
                {/* Spacer below each page */}
                <div
                  className="mx-auto"
                  style={{
                    height: transformedVerticalPageSpacing.y,
                    width: LinearTransform.apply({ x: pageStructure.bounds.width, y: 0 }, transform)
                      .x
                  }}
                >
                  {PageDivider && <PageDivider pageAbove={pageStructure} pageBelow={nextPage} />}
                </div>
              </Fragment>
            )
          })}
        </div>
      </div>
    </LinearTransformProvider>
  )
}

type PDFViewerPageProps = {
  backend: MuPDFRendererBackend
  pageStructure: ResolvedPageStructure
  /**
   * Whether the page based on our current rendering strategy should be rendered
   * At the moment, we render pages in view and a few pages ahead/behind
   */
  shouldRender: boolean

  /**
   * Whether the page is currently in view (includes partial visibility)
   * Not the same as shouldRender because some nearby but out of view pages are pre-rendered
   */
  inViewport: boolean
  annotations: AnnotationBase[]
  toolState: ToolStateBase
  activeAnnotationState: DocumentViewerState['selectionState']
  activeAnnotation: AnnotationBase | undefined
  send: (message: ToolMessage<AnnotationBase, ToolStateBase>) => void
  plugins: AnnotationPlugin<AnnotationBase, ToolStateBase>[]
  PageToolbar?: ComponentType<PageToolbarProps>
  PageDivider?: ComponentType<PageDividerProps>
}

const PDFViewerPageViewer = forwardRef<HTMLDivElement, PDFViewerPageProps>(
  function PDFViewerPageViewer(
    {
      backend,
      pageStructure,
      shouldRender,
      inViewport,
      annotations,
      toolState,
      activeAnnotationState,
      activeAnnotation,
      send,
      plugins,
      PageToolbar
    },
    ref
  ) {
    const transform = useLinearTransform()

    const transformedBounds = LinearTransform.apply(pageStructure.bounds, transform)

    const SelectedToolOverlay = plugins.find((plugin) => plugin.name == toolState.name)?.ToolOverlay

    return (
      <div
        ref={ref}
        className="drawing-canvas group relative mx-auto border"
        data-page-number={pageStructure.pageNumber}
        style={{ width: transformedBounds.width, height: transformedBounds.height }}
      >
        <div className="absolute -top-5 h-5 w-full px-2">
          {inViewport && PageToolbar && <PageToolbar page={pageStructure} />}
        </div>
        {pageStructure.isDeleted ? (
          <div
            className="bg-white"
            style={{
              width: pageStructure.bounds.width,
              height: pageStructure.bounds.height,
              transformOrigin: 'top left',
              transform: LinearTransform.toCSS(transform)
            }}
          >
            <span className="flex h-full items-center justify-center text-xs text-gray-500">
              Deleted Page
            </span>
          </div>
        ) : (
          <div
            style={{
              width: pageStructure.bounds.width,
              height: pageStructure.bounds.height,
              transformOrigin: 'top left',
              transform: LinearTransform.toCSS(transform)
            }}
          >
            {shouldRender ? (
              <RotatedPage
                rotation={pageStructure.rotation}
                sourceBounds={pageStructure.sourceBounds}
              >
                <CanvasImage
                  className="bg-white/70"
                  backend={backend}
                  pageNumber={pageStructure.pageNumber}
                  width={pageStructure.sourceBounds.width}
                  height={pageStructure.sourceBounds.height}
                />
              </RotatedPage>
            ) : (
              <div
                className="flex h-full w-full items-center justify-center bg-white/70 text-gray-500"
                style={{
                  width: pageStructure.bounds.width,
                  height: pageStructure.bounds.height
                }}
              />
            )}

            {/* As an optimization we only render the tool overlay if the page is in view */}
            {/* This is especially important for render heavy plugins like the select tool */}
            {inViewport && SelectedToolOverlay && (
              <SelectedToolOverlay
                page={pageStructure}
                toolState={toolState}
                activeAnnotation={activeAnnotation}
                status={(() => {
                  if (!activeAnnotation) return 'none'
                  if (activeAnnotationState.type === 'none') return 'none'
                  if (activeAnnotationState.annotationId !== activeAnnotation.id) return 'none'
                  return activeAnnotationState.type
                })()}
                send={send}
              />
            )}

            {shouldRender &&
              annotations.map((annotation) => {
                const matchingPlugin = plugins.find(
                  (plugin) => plugin.handlesAnnotation?.(annotation)
                )
                const AnnotationElement = matchingPlugin?.Element

                const status = (() => {
                  if (activeAnnotationState.type === 'none') return 'none'
                  if (activeAnnotationState.annotationId != annotation.id) return 'none'
                  return activeAnnotationState.type
                })()

                return (
                  AnnotationElement && (
                    <AnnotationElement
                      key={annotation.id}
                      annotation={annotation}
                      page={pageStructure}
                      status={status}
                      send={send}
                    />
                  )
                )
              })}
          </div>
        )}
      </div>
    )
  }
)

function RotatedPage({
  rotation,
  sourceBounds,
  children
}: {
  rotation: number
  sourceBounds: Rect
  children: ReactNode
}) {
  const transform = LinearTransform.pageRotate(rotation, sourceBounds)
  const transformedBounds = LinearTransform.apply(sourceBounds, transform)
  return (
    <div
      style={{
        width: transformedBounds.width,
        height: transformedBounds.height,
        transformOrigin: 'top left',
        transform: LinearTransform.toCSS(transform)
      }}
    >
      {children}
    </div>
  )
}

/**
 * Current algorithm to decide what pages to render or pre-render.
 *
 * Render all pages in view and 5 pages ahead.
 *
 * @param pagesInView
 * @param documentStructure
 */
function pagesToRender(pagesInView: number[], pagesStructures: PageStructure[]): Array<number> {
  const allPageNumbers = pagesStructures.map((page) => page.pageNumber)

  const firstPageInView = pagesInView[0]
  const lastPageInView = pagesInView[pagesInView.length - 1]

  const windowMin = firstPageInView - 2
  const windowMax = lastPageInView + 3

  return allPageNumbers.filter((pageNumber) => {
    return pageNumber >= windowMin && pageNumber <= windowMax
  })
}
